import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  ALERT_ICON_TYPE_SUCCESS,
  BORDER_RADIUS,
  FONT_SIZE_BUTTON,
  TIKTOK_BUSINESS,
} from "../../utils/constants";
import {
  AD_ACCOUNTS,
  BUTTON_CONNECTED,
  READY,
  TIKTOK,
} from "../../i18n/keysTranslations";
import ButtonTikTokBusinessAction from "../Buttons/ButtonTikTokBusinessAction";
import { SimpleAlert } from "../Alerts/Alerts";
import Typography from "@mui/material/Typography";
import { startSetLastSelectedIntegrationDataByPlatform } from "../../actions/user";
import { logoPlatform } from "../../utils/string";
import AddIcon from "@mui/icons-material/Add";
import _ from "lodash";
import { INPUT_COLOR_BORDER } from "../../utils/colors";

const TikTokBusinessContainerIntegration = ({
  textConnected = BUTTON_CONNECTED,
  id,
  sx = {},
  onClick = () => {},
  modeConnected = "add",
  modeDisconnected = "verify",
  onChangeIntegration = () => {},
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const onOpenMenu = (e) => setAnchorEl(e.currentTarget);
  const onCloseMenu = () => setAnchorEl(null);

  const dispatch = useDispatchApp();
  const {
    isLoading,
    integrations,
    lastSelectedIntegrationData: {
      [TIKTOK_BUSINESS]: {
        adAccountID: adAccountIDSelected,
        integrationID: integrationIDSelected,
      } = {
        adAccountID: null,
        integrationID: null,
      },
    } = {},
  } = useSelectorApp((state) => state.user);
  const tikTokBusinessIntegrations = integrations?.tiktokBusiness || {};
  const isTikTokBusinessConnected = Object.values(
    tikTokBusinessIntegrations
  ).some((integration) => integration.isActive);

  const { t } = useTranslationApp();

  useEffect(() => {
    if (!integrationIDSelected && !adAccountIDSelected) {
      onPreSelectData();
    }

    onChangeIntegration({
      integrationID: integrationIDSelected,
      adAccountID: adAccountIDSelected,
    });
    // eslint-disable-next-line
  }, [integrationIDSelected, adAccountIDSelected]);

  const onChangeSelectedAdAccount = ({ integrationID, adAccountID }) =>
    dispatch(
      startSetLastSelectedIntegrationDataByPlatform({
        platform: TIKTOK_BUSINESS,
        adAccountID,
        integrationID,
      })
    );

  const onPreSelectData = () => {
    if (_.isEmpty(tikTokBusinessIntegrations)) return;

    const integrationID = Object.keys(tikTokBusinessIntegrations || {})[0];
    const adAccountID = Object.keys(
      tikTokBusinessIntegrations?.[integrationID]?.advertisers || {}
    )[0];

    onChangeSelectedAdAccount({
      integrationID,
      adAccountID,
    });
  };

  if (isLoading) return null;
  return (
    <>
      {isTikTokBusinessConnected ? (
        <Box
          onClick={(e) => {
            onOpenMenu(e);
            onClick(e);
          }}
          sx={{
            backgroundColor: "#fff",
            border: `1px solid ${INPUT_COLOR_BORDER}`,
            display: "flex",
            alignItems: "center",
            px: 2,
            borderRadius: BORDER_RADIUS,
            gap: 1,
            width: "fit-content",
            cursor: "pointer",
            minHeight: 40,
            ...sx,
          }}
        >
          <AvatarMini src={logoPlatform(TIKTOK)} />
          <Typography
            variant="body1"
            sx={{
              minWidth: 80,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "black !important",
              fontSize: FONT_SIZE_BUTTON,
            }}
          >
            {t(textConnected)}
          </Typography>
          <KeyboardArrowDownIcon sx={{ color: "black !important" }} />
          <Box
            sx={{
              display: "flex",
            }}
          >
            <Divider
              orientation="vertical"
              flexItem
              sx={{ backgroundColor: INPUT_COLOR_BORDER }}
            />
            <ButtonTikTokBusinessAction
              id={id}
              type="icon"
              mode={modeConnected}
              icon={<AddIcon sx={{ color: "black !important" }} />}
              disableRipple={true}
              sx={{ mr: -2 }}
              onClick={() => {
                SimpleAlert({
                  title: t(READY),
                  icon: ALERT_ICON_TYPE_SUCCESS,
                });
              }}
            />
          </Box>
        </Box>
      ) : (
        <ButtonTikTokBusinessAction
          id={id}
          mode={modeDisconnected}
          type="button"
          onClick={onClick}
          sx={{
            ...sx,
          }}
        />
      )}
      <Menu anchorEl={anchorEl} open={open} onClose={onCloseMenu}>
        {Object.values(tikTokBusinessIntegrations).map((integration) => (
          <AccountIntegrationMenu
            key={integration.id}
            integration={integration}
          />
        ))}
      </Menu>
    </>
  );
};

const AccountIntegrationMenu = ({ integration = {} }) => {
  const { id, advertisers = {}, name } = integration;
  const advertisersKeys = Object.keys(advertisers);
  const { t } = useTranslationApp();

  const commonSxContainerType = {
    width: "100%",
    mt: 1,
    display: "flex",
    flexDirection: "column",
  };

  return (
    <MenuItem
      disableTouchRipple={true}
      disableRipple={true}
      sx={{
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          backgroundColor: "inherit",
          cursor: "default",
        },
        mb: 0.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          gap: 1,
        }}
      >
        <Typography
          variant="body1"
          textAlign={"left"}
          sx={{
            width: "fit-content",
          }}
        >
          {name || id}
        </Typography>
      </Box>
      {advertisersKeys.length > 0 && (
        <Box sx={commonSxContainerType}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
            }}
          >
            {t(AD_ACCOUNTS)}
          </Typography>
          {advertisersKeys.map((key) => (
            <Box
              key={advertisers[key].id}
              sx={{
                display: "flex",
                flexDirection: "column",
                mt: 1,
                pl: 3,
              }}
            >
              <Typography variant="body1">{advertisers[key].name}</Typography>
              <Typography
                variant="caption"
                sx={{
                  mt: -0.5,
                }}
              >
                {advertisers[key].id}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </MenuItem>
  );
};

const AvatarMini = ({ src, sx }) => (
  <Avatar
    alt="Logo de TikTok"
    src={src}
    variant="square"
    sx={{
      height: 20,
      width: 20,
      borderRadius: BORDER_RADIUS,
      "& .MuiAvatar-img": {
        objectFit: "contain",
      },
      ...sx,
    }}
  />
);

export default TikTokBusinessContainerIntegration;
